import React, { useEffect, useState } from 'react';


import { useTypedTranslation } from '@app/hooks';
import { Link } from '@material-ui/core';
import { PasslySelect, PasslyTextField } from '@app/components';
import { useSelector } from 'react-redux';
import { syncService, provisioningPoliciesService } from '@app/services';
import { IProvisioningPolicyDTO } from '@app/models';
import { AxiosError } from 'axios';
import { localStore } from '@app/helpers';

interface IProps {
    onNext: Function;
    onCancel: Function;
    agentId: string;
}

const ConfigureAgent = (props: IProps) => {
    const localStorageOrganization = localStore.getItem('org_filter') ? localStore.getItem('org_filter') : '';

    const availableOrgs = useSelector((state: any) => state.basic.shellConfig.organizations);
    const [agentLoaded, setAgentLoaded] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(localStorageOrganization);
    const [selectedPolicy, setSelectedPolicy] = useState('');
    const [availablePolicies, setAvailablePolicies] = useState<IProvisioningPolicyDTO[]>([]);
    const [agent, setAgent] = useState<any>({});
    const [agentName, setAgentName] = useState('');
    const [agentError, setAgentError] = useState('');
    const [processingAgent, setProcessingAgent] = useState(false);

    const { translation } = useTypedTranslation();

    useEffect(() => {
        // Load agent default
        syncService.getSyncAgent(props.agentId)
            .then((response: any) => {
                setAgent(response.data);
                setAgentName(response.data.AgentName);
                setAgentLoaded(true);
            });

        provisioningPoliciesService.getProvisioningPoliciesDTO()
            .then((response: any) => {
                if (response.data && response.data.value && response.data.value.length) {
                    setAvailablePolicies(response.data.value as IProvisioningPolicyDTO[]);
                    setSelectedPolicy(response.data.value[0].Id);
                }
            });

        if (localStorageOrganization !== '' && localStorageOrganization !== null) {
            setSelectedOrg(localStorageOrganization);
        }
    }, []);

    const handleOrgChange = () => (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedOrg(event.target.value as string);
    };

    const handlePolicyChange = () => (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedPolicy(event.target.value as string);
    };

    const handleNameChange = () => (event: React.ChangeEvent<HTMLInputElement>) => {
        setAgentName(event.target.value as string);
    };

    const trySubmitAgent = () => {
        setProcessingAgent(true);

        agent.AgentName = agentName;
        agent.ProvisioningState = 'Active';
        agent.StatusAction = 'Provisioned';
        agent.SendActivationEmail = false;
        agent.EnforceMFA = false;
        agent.OrganizationId = selectedOrg;
        agent.ProvisioningPolicyId = selectedPolicy;

        syncService.putSyncAgent(agent, props.agentId, handleSuccess, handleError);
    };

    const handleSuccess = () => {
        setProcessingAgent(false);
        props.onNext();
    };

    const handleError = (error: AxiosError<any>) => {
        setProcessingAgent(false);
        if (error.response && error.response.status === 409) {
            setAgentError(translation('AGENT_PROFILE_NAME_ALREADY_EXISTS'));
        }
        else {
            setAgentError(translation('EDIT_AGENT_ERROR'));
        }
    };

    return (
        <>
            <div className='rowcontainer'>
                <div className='dirsync__agent'>
                    <img src='/assets/images/directorysync/06-sync-settings.png' alt=''/>
                    {agentLoaded &&
                        <div className='dirsync__agent--content'>
                            <h2>{translation('DIRSYNC_CONFIGSYNC_TITLE')}</h2>
                            <p>{translation('DIRSYNC_CONFIGSYNC_TEXT')}</p>
                            <p>{translation('DIRSYNC_GETSTARTED_TEXT')}</p>

                            <div className='dirsync__settings'>
                                <div className='dirsync__agent--label'>{translation('DIRSYNC_CONFIGSYNC_ORGANIZATION')}</div>
                                <div>
                                    <PasslySelect
                                        id=''
                                        label={translation('SELECTED_ORGANIZATION')}
                                        value={selectedOrg}
                                        onChange={handleOrgChange()}
                                        options={availableOrgs}
                                        optionLabel='name'
                                        optionValue='id'
                                    />
                                </div>
                            </div>

                            <div className='dirsync__settings'>
                                <div className='dirsync__agent--label'>{translation('DIRSYNC_CONFIGSYNC_AGENTNAME')}</div>
                                <div>
                                    <PasslyTextField
                                        id=''
                                        fullWidth
                                        required
                                        error={agentName === ''}
                                        label={translation('AGENT_NAME')}
                                        value={agentName}
                                        autoFocus={true}
                                        onChange={handleNameChange()}
                                    />
                                </div>
                            </div>

                            <div className='dirsync__settings'>
                                <div className='dirsync__agent--label'>{translation('DIRSYNC_CONFIGSYNC_POLICY')}</div>
                                <div>
                                    <PasslySelect
                                        id=''
                                        label={translation('PROVISIONING_POLICY')}
                                        value={selectedPolicy}
                                        onChange={handlePolicyChange()}
                                        options={availablePolicies}
                                        optionLabel='DisplayName'
                                        optionValue='Id'
                                    />
                                </div>
                            </div>

                            {agentError !== '' &&
                                <div>{agentError}</div>
                            }
                            <div className='dirsync__bottom--links'>
                                <Link component='button' onClick={() => props.onCancel()}>
                                    {translation('DIRSYNC_CANCEL')}
                                </Link>
                                <Link disabled={processingAgent} component='button' onClick={() => trySubmitAgent()}>
                                    {translation('DIRSYNC_NEXT')}
                                </Link>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </>
    );
};

export default ConfigureAgent;